import React, { useState, useEffect } from 'react';
import { Container, Typography, Button, Box, Paper, Fab } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ConveniosTable from './ConveniosTable';
import ConveniosForm from './ConveniosForm';
import DeleteConveniosDialog from './DeleteConveniosDialog';
import {
    fetchConvenios,
    createConvenio,
    updateConvenio,
    deleteConvenio
} from '../../services/empleados.api';
import { useSnackbar } from 'notistack'; // Assuming notistack is available in package.json
import Navbar from '../../../../../Navbar';
import Title from '../../../../../Title';
import { useDebounce } from '../../../../../../Hooks/UseDebounce';

const ConveniosPage = () => {
    const [convenios, setConvenios] = useState([]);
    const [loading, setLoading] = useState(false);
    const [formOpen, setFormOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [currentConvenio, setCurrentConvenio] = useState(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [total, setTotal] = useState(0);
    const [filters, setFilters] = useState({
        nombre: ''
    });
    const { enqueueSnackbar } = useSnackbar();

    const debouncedFilters = useDebounce(filters, 500);

    useEffect(() => {
        loadConvenios({ 
            page: page + 1, 
            rowsPerPage: rowsPerPage, 
            ...debouncedFilters 
        });
    }, [page, rowsPerPage, debouncedFilters]);

    const loadConvenios = async (params = {}) => {
        setLoading(true);
        try {
            const data = await fetchConvenios(params);
            if (data.data) {
                setConvenios(data.data);
                setTotal(parseInt(data.total) || data.data.length);
            } else {
                setConvenios(data || []);
                setTotal(data.length || 0);
            }
        } catch (error) {
            console.error("Error loading convenios:", error);
            enqueueSnackbar('Error al cargar convenios', { variant: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (column, value) => {
        setFilters(prev => ({ ...prev, [column]: value }));
    };

    useEffect(() => {
        setPage(0);
    }, [debouncedFilters]);

    const handleOpenCreate = () => {
        setCurrentConvenio(null);
        setFormOpen(true);
    };

    const handleOpenEdit = (convenio) => {
        setCurrentConvenio(convenio);
        setFormOpen(true);
    };

    const handleOpenDelete = (convenio) => {
        setCurrentConvenio(convenio);
        setDeleteOpen(true);
    };

    const getUser = () => {
        const user = localStorage.getItem("user");
        return user || "unknown";
    };

    const handleFormSubmit = async (formData) => {
        try {
            const user = getUser();
            const payload = { ...formData, username: user };

            if (currentConvenio) {
                await updateConvenio(currentConvenio.id, payload);
                enqueueSnackbar('Convenio actualizado correctamente', { variant: 'success' });
            } else {
                await createConvenio(payload);
                enqueueSnackbar('Convenio creado correctamente', { variant: 'success' });
            }
            setFormOpen(false);
            loadConvenios({ page: page + 1, rowsPerPage: rowsPerPage, ...filters });
        } catch (error) {
            console.error("Error saving convenio:", error);
            enqueueSnackbar('Error al guardar el convenio', { variant: 'error' });
        }
    };

    const handleDeleteConfirm = async () => {
        if (!currentConvenio) return;
        try {
            await deleteConvenio(currentConvenio.id);
            enqueueSnackbar('Convenio eliminado correctamente', { variant: 'success' });
            setDeleteOpen(false);
            loadConvenios({ page: page + 1, rowsPerPage: rowsPerPage, ...filters });
        } catch (error) {
            console.error("Error deleting convenio:", error);
            enqueueSnackbar('Error al eliminar el convenio', { variant: 'error' });
        }
    };

    return (
        <Container maxWidth="xl" sx={{ mt: 2, mb: 4 }}>
            <Navbar />
            <Title titulo="Gestión de Convenios" handleClose={() => window.history.back()} />
            
            <Box display="flex" justifyContent="flex-end" alignItems="center" mt={2} mb={2}>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={handleOpenCreate}
                    sx={{ 
                        borderRadius: 2, 
                        textTransform: 'none', 
                        boxShadow: '0 4px 14px 0 rgba(0,118,255,0.39)',
                        px: 3,
                        '&:hover': {
                            boxShadow: '0 6px 20px rgba(0,118,255,0.23)',
                        }
                    }}
                >
                    Nuevo Convenio
                </Button>
            </Box>

            <Paper elevation={4} sx={{ borderRadius: 3, overflow: 'hidden', border: '1px solid rgba(0,0,0,0.08)' }}>
                <ConveniosTable
                    data={convenios}
                    loading={loading}
                    onEdit={handleOpenEdit}
                    onDelete={handleOpenDelete}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    total={total}
                    onPageChange={(e, newPage) => setPage(newPage)}
                    onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value, 10));
                        setPage(0);
                    }}
                    filters={filters}
                    onFilterChange={handleFilterChange}
                />
            </Paper>

            <ConveniosForm
                open={formOpen}
                handleClose={() => setFormOpen(false)}
                handleSubmit={handleFormSubmit}
                initialData={currentConvenio}
            />

            <DeleteConveniosDialog
                open={deleteOpen}
                handleClose={() => setDeleteOpen(false)}
                handleConfirm={handleDeleteConfirm}
                convenioName={currentConvenio?.nombre}
            />
        </Container>
    );
};

export default ConveniosPage;
